/**
 * Public Signal Approval Receipt V1 - Main Receipt Generator
 * 
 * Creates deterministic approval receipts for public signal drafts
 * before any external posting or sharing.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - No auto-posting capability
 * - Deterministic output only
 * - Fail closed on forbidden exposure patterns
 * - No mutation of inputs
 * 
 * INVARIANT:
 * Public signal approval records human approval for communication only.
 * It does not publish automatically.
 */

// NOTE: crypto stubbed for preview deployment (browser-safe)
const createHashStub = (input: string) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(32, '0');
};
import {
  PublicSignalApprovalInputV1,
  PublicSignalApprovalReceiptV1,
  ApprovalDecision,
} from './types';
import { validateDraft } from './draftValidator';
import { scanEditedDraft } from './editedDraftScanner';

/**
 * Compute deterministic final draft hash
 */
function computeFinalDraftHash(
  sourceDraftHash: string,
  editedDraftText: string | undefined
): string {
  // If no edits, return source hash directly
  if (!editedDraftText) {
    return sourceDraftHash;
  }

  const hashInput = JSON.stringify({
    sourceDraftHash,
    editedDraftText,
  }, Object.keys({sourceDraftHash, editedDraftText}).sort());

  return createHashStub(hashInput);
}

/**
 * Compute deterministic approval hash
 */
function computeApprovalHash(
  sourceDraftHash: string,
  finalDraftHash: string,
  decision: ApprovalDecision,
  decisionReason: string,
  approverId: string,
  eligibleForPublishing: boolean
): string {
  const hashInput = JSON.stringify({
    sourceDraftHash,
    finalDraftHash,
    decision,
    decisionReason,
    approverId,
    eligibleForPublishing,
  }, Object.keys({sourceDraftHash, finalDraftHash, decision, decisionReason, approverId, eligibleForPublishing}).sort());

  return createHashStub(hashInput);
}

/**
 * Generate approval timestamp (deterministic based on input)
 * Note: Using a deterministic timestamp based on hash to avoid Date.now()
 */
function generateApprovalTimestamp(sourceDraftHash: string): string {
  // Use the hash to generate a deterministic timestamp
  // This is not a real timestamp but a deterministic identifier
  const hash = createHashStub(sourceDraftHash);
  const timestamp = parseInt(hash.substring(0, 8), 16);
  const date = new Date(timestamp * 1000);
  return date.toISOString();
}

/**
 * Generate public signal approval receipt
 */
export function generatePublicSignalApprovalReceipt(
  input: PublicSignalApprovalInputV1
): PublicSignalApprovalReceiptV1 {
  const { draft, approverId, decision, decisionReason, editedDraftText } = input;

  // Validate approverId is present
  if (!approverId || approverId.trim() === '') {
    throw new Error('approverId is required');
  }

  // Validate decisionReason is present
  if (!decisionReason || decisionReason.trim() === '') {
    throw new Error('decisionReason is required');
  }

  // Validate draft
  const draftValidation = validateDraft(draft);
  if (!draftValidation.valid) {
    throw new Error(`Draft validation failed: ${draftValidation.reason}`);
  }

  // Determine if edited
  const edited = editedDraftText !== undefined && editedDraftText !== draft.draftText;

  // Scan edited draft if provided
  if (editedDraftText && editedDraftText !== draft.draftText) {
    const scanResult = scanEditedDraft(editedDraftText);
    
    // If approved, edited draft must be safe
    if (decision === 'approved' && !scanResult.safe) {
      throw new Error(
        `Approved edited draft contains forbidden terms: ${scanResult.forbiddenTerms.join(', ')}`
      );
    }

    // For any decision, log forbidden terms but only fail if approved
    if (!scanResult.safe && decision === 'approved') {
      throw new Error(
        `Edited draft contains forbidden terms and cannot be approved: ${scanResult.forbiddenTerms.join(', ')}`
      );
    }
  }

  // Determine eligibleForPublishing based on decision
  const eligibleForPublishing = decision === 'approved';

  // Compute source draft hash (use existing draft hash)
  const sourceDraftHash = draft.draftHash;

  // Compute final draft hash
  const finalDraftHash = computeFinalDraftHash(sourceDraftHash, editedDraftText);

  // Compute approval hash
  const approvalHash = computeApprovalHash(
    sourceDraftHash,
    finalDraftHash,
    decision,
    decisionReason,
    approverId,
    eligibleForPublishing
  );

  // Generate approval timestamp
  const approvedAt = generateApprovalTimestamp(sourceDraftHash);

  // Build receipt
  const receipt: PublicSignalApprovalReceiptV1 = {
    sourceDraftHash,
    finalDraftHash,
    decision,
    decisionReason,
    approverId,
    eligibleForPublishing,
    approvalHash,
    approvedAt,
    edited,
  };

  return receipt;
}

/**
 * Check if receipt is eligible for publishing
 */
export function isEligibleForPublishing(receipt: PublicSignalApprovalReceiptV1): boolean {
  return receipt.eligibleForPublishing;
}

/**
 * Verify receipt integrity
 */
export function verifyReceiptIntegrity(receipt: PublicSignalApprovalReceiptV1): boolean {
  // Recompute approval hash
  const computedHash = computeApprovalHash(
    receipt.sourceDraftHash,
    receipt.finalDraftHash,
    receipt.decision,
    receipt.decisionReason,
    receipt.approverId,
    receipt.eligibleForPublishing
  );

  return computedHash === receipt.approvalHash;
}

/**
 * Compute approval hash (exported for testing)
 */
export { computeApprovalHash };
